// Simple global toast utility
export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  duration?: number;
}

let toastContainer: HTMLElement | null = null;

const createToastContainer = (): HTMLElement => {
  if (toastContainer) return toastContainer;
  
  toastContainer = document.createElement('div');
  toastContainer.className = 'toast-container';
  toastContainer.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 400px;
    pointer-events: none;
  `;
  document.body.appendChild(toastContainer);
  return toastContainer;
};

const getIcon = (type: ToastType): string => {
  switch (type) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'warning':
      return '⚠';
    case 'info':
      return 'ℹ';
    default:
      return 'ℹ';
  }
};

const getColors = (type: ToastType): { bg: string; border: string; color: string } => {
  switch (type) {
    case 'success':
      return { bg: '#d4edda', border: '#c3e6cb', color: '#155724' };
    case 'error':
      return { bg: '#f8d7da', border: '#f5c6cb', color: '#721c24' };
    case 'warning':
      return { bg: '#fff3cd', border: '#ffeaa7', color: '#856404' };
    case 'info':
      return { bg: '#d1ecf1', border: '#bee5eb', color: '#0c5460' };
    default:
      return { bg: '#d1ecf1', border: '#bee5eb', color: '#0c5460' };
  }
};

export const showToast = (message: string, type: ToastType = 'info', options: ToastOptions = {}): void => {
  console.log('showToast called:', { message, type, options });
  
  const container = createToastContainer();
  const { duration = 5000 } = options;
  const colors = getColors(type);
  const icon = getIcon(type);
  
  const toast = document.createElement('div');
  toast.style.cssText = `
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideInFromRight 0.3s ease-out;
    min-width: 300px;
    max-width: 400px;
    background-color: ${colors.bg};
    border: 1px solid ${colors.border};
    color: ${colors.color};
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    margin-bottom: 10px;
  `;
  
  toast.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
      <span style="font-size: 16px; flex-shrink: 0;">${icon}</span>
      <span style="line-height: 1.4; word-wrap: break-word;">${message}</span>
    </div>
    <button style="
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 8px;
      opacity: 0.7;
      font-size: 14px;
    " onclick="this.parentElement.remove()">✕</button>
  `;
  
  // Add CSS animation
  if (!document.querySelector('#toast-animations')) {
    const style = document.createElement('style');
    style.id = 'toast-animations';
    style.textContent = `
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }
  
  container.appendChild(toast);
  
  // Auto-remove after duration
  setTimeout(() => {
    if (toast.parentElement) {
      toast.remove();
    }
  }, duration);
  
  console.log('Toast element created and added to DOM');
};